/** Current application status — mirrors Loan.admin_status (headline only). */
export type PartnerApplicationStatus =
  | "registered"
  | "business_docs_in_progress"
  | "business_docs_submitted"
  | "business_docs_input_required"
  | "business_docs_verified"
  | "kyc_in_progress"
  | "kyc_submitted"
  | "kyc_input_required"
  | "kyc_verified"
  | "credit_review"
  | "limit_offered"
  | "settlement_bank_updated"
  | "settlement_bank_verified"
  | "virtual_account_created"
  | "agreements_signed"
  | "account_change_verified"
  | "sanctioned"
  | "active"
  | "closed"
  | "credit_rejected";

export type DocumentCategory =
  | "financial_docs"
  | "data_collection"
  | "business_kyc"
  | "personal_kyc"
  | "shareholder_kyc";

export type ApplicationSummary = {
  uuid: string;
  loan_identifier: string;
  merchant_name: string;
  merchant_email: string;
  company_name: string;
  status: PartnerApplicationStatus;
  status_display: string;
  /** Present only for input-required statuses. */
  status_remarks?: string | null;
  created: string;
  modified: string;
};

export type DocumentFileItem = {
  uuid: string;
  file_name: string;
  file_url?: string | null;
  uploaded_at?: string | null;
  uploaded_by?: string | null;
};

export type DocumentChecklistItem = {
  document_key: string;
  document_type_id?: number;
  document_uuid?: string | null;
  label: string;
  /** Same help text the merchant portal shows (from application document mappings). */
  description?: string | null;
  category: DocumentCategory;
  required: boolean;
  multi_upload: boolean;
  uploaded: boolean;
  status?: string | null;
  files: DocumentFileItem[];
  /** @deprecated use files */
  file_name?: string | null;
  uploaded_at?: string | null;
};

export type ExistingLendingLine = {
  lender_name: string;
  loan_amount: number | null;
  facility_type: string;
  outstanding?: number | null;
  emi?: number | null;
  remarks?: string;
};

export type StatusTimelineEntry = {
  occurred_at: string;
  status: PartnerApplicationStatus | string;
  status_display: string;
  /** Present only for input-required statuses. */
  remarks?: string | null;
};

export type ApplicationDetail = ApplicationSummary & {
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
  country_code: string;
  company_website: string;
  company_type: string;
  company_type_label: string;
  business_type: string;
  type_of_financing?: string | null;
  platform?: string | null;
  platform_name?: string | null;
  loan_amount_requested?: string | number | null;
  company_business_brief?: string | null;
  last_12_month_revenue?: string | number | null;
  pat_percent?: string | number | null;
  business_vintage_years?: string | number | null;
  files_vat?: boolean | null;
  vat_number?: string | null;
  no_existing_loans?: boolean | null;
  existing_lending_lines?: ExistingLendingLine[];
  financial_docs_editable?: boolean;
  checklist?: DocumentChecklistItem[];
  status_timeline?: StatusTimelineEntry[];
};

export type FinancingDetailsPayload = {
  type_of_financing?: string;
  platform?: string;
  platform_name?: string;
  loan_amount_requested?: string | number | null;
  company_business_brief?: string;
  last_12_month_revenue?: string | number | null;
  pat_percent?: string | number | null;
  business_vintage_years?: string | number | null;
  files_vat?: boolean | null;
  vat_number?: string | null;
  no_existing_loans?: boolean;
  existing_lending_lines?: ExistingLendingLine[];
};

/** Matches admin create-lead modal fields. company_type is IndustrySector id. */
export type LeadFormPayload = {
  company_name: string;
  company_type: string;
  company_website: string;
  first_name: string;
  last_name: string;
  phone_number: string;
  country_code: string;
  email: string;
};

export type CompanyTypeOption = {
  id: number;
  name: string;
};

export type CreateApplicationPayload = LeadFormPayload;

export type UpdateApplicationPayload = LeadFormPayload;

export type ApplicationListParams = {
  q?: string;
  status?: PartnerApplicationStatus | "all";
  page?: number;
};

export type ApplicationListResponse = {
  count: number;
  page: number;
  total_pages: number;
  page_size: number;
  results: ApplicationSummary[];
};

export const PARTNER_STATUS_LABELS: Record<PartnerApplicationStatus, string> = {
  registered: "Registered",
  business_docs_in_progress: "Business Docs in Progress",
  business_docs_submitted: "Business Docs Submitted",
  business_docs_input_required: "Business Docs Input Required",
  business_docs_verified: "Business Docs Verified",
  kyc_in_progress: "KYC In Progress",
  kyc_submitted: "KYC Submitted",
  kyc_input_required: "KYC Input Required",
  kyc_verified: "KYC Verified",
  credit_review: "Credit Review",
  limit_offered: "Limit Offered",
  settlement_bank_updated: "Settlement Bank Updated",
  settlement_bank_verified: "Settlement Bank Verified",
  virtual_account_created: "Virtual Account Created",
  agreements_signed: "Agreements Signed",
  account_change_verified: "Account Change Verified",
  sanctioned: "Sanctioned",
  active: "Active",
  closed: "Closed",
  credit_rejected: "Credit Rejected",
};

export const DOCUMENT_CATEGORY_LABELS: Record<DocumentCategory, string> = {
  financial_docs: "Financial documents",
  data_collection: "Financial documents",
  business_kyc: "Business KYC",
  personal_kyc: "Owner KYC",
  shareholder_kyc: "Shareholder KYC",
};
